import { useCallback, useMemo, useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import { InputContainer } from "uikit-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { AppContexts } from "../../providers";
import { InputRadio, Form, FormRow, FormInputWrapper } from "../../ui";
import ConvertUTCDateToLocalDate from "../common/ConvertUTCDateToLocalDate";

const schema = yup.object().shape({
  field: yup.string().required("Field Name is required"),
  macro: yup.string().required("Macro is required"),
});

const defaultValues = {
  field: "",
  macro: "",
};

const MacroAddCustomField = ({ macro }) => {
  const { set, get, settings } = useContext(
    AppContexts.SettingsContext,
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });
  const offCanvasRef = useRef(null);
  const macroManuallyEditedRef = useRef(false);
  const fieldValue = watch("field");

  const checkDuplicates = (m) => {
    let customMacros = [...(settings.custom_macros || [])];
    if (macro) {
      customMacros = customMacros.filter((cm) => cm.id !== macro.id);
    }
    if (customMacros?.find((cm) => cm.field === m.field)) {
      return 'field';
    } else if (customMacros?.find((cm) => cm.macro === m.macro)) {
      return 'macro'
    } else {
      return null
    }
  }

  const formatMacroname = (macro) => {
    return `[${macro
        .replace(/\b[a-z](?=[a-z]{2})/g, (letter) => letter.toUpperCase())
        .replace(/[\W_]+/g, "")}]`;
  }

  // Handle form submission
  const handleRegister = async (formData) => {
    const newMacro = {
      category: 'Unsynced',
      field: formData.field,
      macro: formatMacroname(formData.macro),
    }
    let newMacros = [...(settings.custom_macros || [])];
    const duplicatedField = checkDuplicates(newMacro);
    if (duplicatedField === 'field') {
      setError('field', { type: 'custom', message: 'Macro already exists' });
    } else if (duplicatedField === 'macro') {
      setError('macro', { type: 'custom', message: 'Macro already exists' });
    } else {
      if (macro) {
        let idx = newMacros.findIndex(
          (nm) => nm.id.toString() === macro.id.toString(),
        );
        newMacros[idx] = { ...macro, ...newMacro };
      } else {
        newMacros.push(newMacro);
      }
      
      await set(["custom_macros"], {
        custom_macros: newMacros,
      }).then(async (res) => {
        await get(["custom_macros"]);
        UIkit.offcanvas(offCanvasRef.current).hide();
        macroManuallyEditedRef.current = false;
        setValue('field', '');
        setValue('macro', '');
      });
      macroManuallyEditedRef.current = false;
      setValue('field', '');
      setValue('macro', '');
    }
  };

  useEffect(() => {
    if (macro) {
      macroManuallyEditedRef.current = macro.macro !== formatMacroname(macro.field || "");
      setValue('field', macro.field);
      setValue('macro', macro.macro);
    } else {
      macroManuallyEditedRef.current = false;
      setValue('field', '');
      setValue('macro', '');
    }
  }, [macro])

  useEffect(() => {
    if (macroManuallyEditedRef.current) return;
    setValue('macro', fieldValue ? formatMacroname(fieldValue) : '');
  }, [fieldValue, setValue])

  return (
    <div
      id="add-custom-field-modal"
      ref={offCanvasRef}
      className="uk-modal-full hms-modals uk-modal uk-open"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
          />
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {!macro ? "Add a" : "Update"} Custom Field
            </h2>
          </div>
          <div className="uk-modal-body uk-flex">
            <Form
              onSubmit={handleSubmit(handleRegister)}
              className="uk-grid-small"
              uk-grid="true"
            >
              <p>Add/Edit a field to set values for each unit. This macro can be used in scheduled messages and digital guestbooks.</p>
              
              <FormRow>
                <FormInputWrapper label="Field Name*" size="two">
                  <InputContainer>
                    <input
                      className={classnames("uk-input", { error: errors.field })}
                      type="text"
                      {...register("field")}
                    />
                  </InputContainer>
                  <div className="uk-label-danger">
                    {errors.field?.message}
                  </div>
                </FormInputWrapper>
              </FormRow>
              <FormRow>
                <FormInputWrapper label="Macro*" size="two">
                  <InputContainer>
                    <input
                      className={classnames("uk-input", { error: errors.macro })}
                      type="text"
                      {...register("macro")}
                      onChange={(e) => {
                        macroManuallyEditedRef.current = true;
                        setValue('macro', formatMacroname(e.target.value));
                      }}
                    />
                  </InputContainer>
                  <div className="uk-label-danger">
                    {errors.macro?.message}
                  </div>
                </FormInputWrapper>
              </FormRow>

              <div>
                <button className="uk-button hms-btn hms-red-btn" type="submit">
                  {macro ? "Save" : "Add"}
                </button>
                <button
                  className="uk-button hms-btn uk-offcanvas-close"
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacroAddCustomField;
